import type { ReactNode } from "react";
import { useAuth } from "@/auth/AuthContext";
import { Empty } from "@/components/DataState";

/**
 * A screen the caller may not have.
 *
 * The nav already hides links people cannot use; this is for the case where
 * somebody arrives by URL. It says what is missing rather than bouncing them,
 * because "you need instances.read" is actionable and a redirect is not.
 */
export default function Guard({
  perms, children,
}: { perms: string[]; children: ReactNode }) {
  const { can } = useAuth();
  if (perms.length && !can(...perms)) {
    return (
      <Empty
        title="You do not have access to this"
        hint={`This screen needs ${perms.join(" and ")}. Ask an administrator to grant it.`}
      />
    );
  }
  return <>{children}</>;
}

/** Render children only when the caller holds every permission. */
export function Can({ perms, children }: { perms: string[]; children: ReactNode }) {
  const { can } = useAuth();
  if (perms.length && !can(...perms)) return null;
  return <>{children}</>;
}
